import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  sendAnalyticsEvent,
  buildAnalyticsPayload,
  extractSuperPropertiesFromUrl,
  getStoredSuperProperties,
  storeSuperProperties,
  type SuperProperties,
} from '@/services/AnalyticsService';
import type { GameData } from '@/interfaces';

interface AnalyticsContextValue {
  track: (eventName: string, properties?: Record<string, unknown>) => void;
  superProperties: SuperProperties;
  isEnabled: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface AnalyticsProviderProps {
  children: React.ReactNode;
  gameData: GameData | null;
}

/**
 * Extracts analytics parameters from URL and provides tracking functionality.
 * Tracking is disabled if analytics_webhook_url is not configured in gameData.
 */
export function AnalyticsProvider({ children, gameData }: AnalyticsProviderProps) {
  const hasTrackedCloseRef = useRef(false);

  // Clear session timeout flag when checkout starts
  useEffect(() => {
    try {
      sessionStorage.removeItem('checkout_session_expired');
    } catch {
      // Ignore sessionStorage errors
    }
  }, []);

  const urlSuperProperties = useMemo<SuperProperties>(() => {
    return extractSuperPropertiesFromUrl();
  }, []);

  const [storedSuperProperties, setStoredSuperProperties] = useState<SuperProperties>(
    () => getStoredSuperProperties()
  );

  const superProperties = useMemo<SuperProperties>(
    () => ({
      checkout_id:
        urlSuperProperties.checkout_id || storedSuperProperties.checkout_id,
      distinct_id:
        urlSuperProperties.distinct_id || storedSuperProperties.distinct_id,
      transaction_id:
        urlSuperProperties.transaction_id || storedSuperProperties.transaction_id,
    }),
    [urlSuperProperties, storedSuperProperties]
  );

  useEffect(() => {
    if (
      urlSuperProperties.checkout_id ||
      urlSuperProperties.distinct_id ||
      urlSuperProperties.transaction_id
    ) {
      storeSuperProperties(urlSuperProperties);
      setStoredSuperProperties(getStoredSuperProperties());
    }
  }, [urlSuperProperties]);

  const webhookUrl = gameData?.analytics_webhook_url;
  const isEnabled = !!webhookUrl;

  const track = useCallback(
    (eventName: string, properties: Record<string, unknown> = {}) => {
      sendAnalyticsEvent(webhookUrl, eventName, superProperties, properties);
    },
    [webhookUrl, superProperties]
  );

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!hasTrackedCloseRef.current && isEnabled && webhookUrl && navigator.sendBeacon) {
        hasTrackedCloseRef.current = true;
        
        // Check if checkout was closed due to session timeout
        let isTimeout = false;
        try {
          isTimeout = sessionStorage.getItem('checkout_session_expired') === 'true';
        } catch {
          // Ignore sessionStorage errors
        }
        
        const payload = buildAnalyticsPayload('checkout_close', superProperties, {
          is_timeout: isTimeout,
        });
        navigator.sendBeacon(webhookUrl, JSON.stringify(payload));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [webhookUrl, superProperties, isEnabled]);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      track,
      superProperties,
      isEnabled,
    }),
    [track, superProperties, isEnabled]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);

  if (!context) {
    return {
      track: () => {},
      superProperties: {},
      isEnabled: false,
    };
  }

  return context;
}
